// Owns the steering wheel: finds it (WebHID for Logitech wheels with force
// feedback, the Gamepad API as an input-only fallback), applies the saved
// bindings, and runs the force feedback output loop.
//
// The FFB loop runs on its own ~250 Hz timer rather than once per frame, so
// vibration effects render cleanly and a dropped frame doesn't stall the
// wheel. Each tick samples the force model and hands the latest value to the
// device, which only writes it if it changed.

import { FAKE_WHEELS, type FakeWheelModel } from './descriptors';
import { LOGITECH_VENDOR_ID, WHEEL_PRODUCTS, isWheel, ledMask } from './g27';
import { GamepadSource, looksLikeWheel } from './gamepadSource';
import { HidWheel } from './hidWheel';
import { ForceFeedback, type FfbInputs } from './ffb';
import { WheelInput, defaultProfile, loadProfile, saveProfile, type WheelInputState, type WheelProfile } from './bindings';
import { wheelParams } from './settings';
import type { ControlSource } from './source';

export type WheelStatus = 'none' | 'hid' | 'gamepad' | 'no-webhid';

const FFB_TICK_MS = 4;

export class WheelManager {
  source: ControlSource | undefined;
  hid: HidWheel | undefined;
  readonly ffb = new ForceFeedback();
  input: WheelInput = new WheelInput(loadProfile('none'));
  /** Latest mapped state (updated by poll()). */
  state: WheelInputState | undefined;
  /** The game is driving: forces on. Otherwise the wheel idles with a gentle centring spring. */
  private driving = false;
  /** Called whenever the connected device changes. */
  onChange: (() => void) | undefined;
  /** Last problem worth showing (permission, open failure). */
  error: string | undefined;
  /** Test/override force (setup screen), −1..1 in game convention, until `overrideUntil` (ms). */
  private override = 0;
  private overrideUntil = 0;
  private timer: ReturnType<typeof setInterval> | undefined;
  private ledFlash = false;
  private overrideActive = false;
  private calibrating = false;

  get supported(): boolean {
    return typeof navigator !== 'undefined' && !!navigator.hid;
  }

  get status(): WheelStatus {
    if (this.hid?.connected) return 'hid';
    if (this.source?.connected) return 'gamepad';
    return this.supported ? 'none' : 'no-webhid';
  }

  /** A wheel is connected and should drive the car (not a G29 left on its PS4 switch, which we can only point out). */
  get active(): boolean {
    return !!this.source?.connected && !this.hid?.ps4Mode;
  }

  get hasFfb(): boolean {
    return !!this.hid?.connected && this.hid.native;
  }

  get profile(): WheelProfile {
    return this.input.profile;
  }

  /** Look for wheels we already have permission for, and watch for plugging in and out. */
  async init(): Promise<void> {
    if (typeof window === 'undefined') return;
    window.addEventListener('pagehide', () => this.hid?.emergencyStop());
    window.addEventListener('gamepadconnected', (e) => {
      const pad = (e as GamepadEvent).gamepad;
      if (!this.source && looksLikeWheel(pad.id)) this.use(new GamepadSource(pad.index, pad.id));
    });
    window.addEventListener('gamepaddisconnected', (e) => {
      const pad = (e as GamepadEvent).gamepad;
      if (this.source instanceof GamepadSource && this.source.index === pad.index) this.use(undefined);
    });
    const hid = navigator.hid;
    if (!hid) return;
    hid.addEventListener('connect', (e) => {
      if (isWheel(e.device) && !this.hid?.connected) void this.openHid(e.device);
    });
    hid.addEventListener('disconnect', (e) => {
      if (this.hid && e.device === this.hid.device) {
        this.hid.markDisconnected();
        this.use(undefined);
      }
    });
    try {
      const devices = await hid.getDevices();
      const wheel = devices.find(isWheel);
      if (wheel) await this.openHid(wheel);
    } catch {
      // Permissions policy can block WebHID (e.g. inside an iframe).
    }
  }

  /** Ask for a Logitech wheel over WebHID. Needs a user gesture. */
  async request(): Promise<boolean> {
    const hid = navigator.hid;
    if (!hid) {
      this.error = 'This browser has no WebHID. Use Chrome, Edge or Chromium for force feedback.';
      return false;
    }
    try {
      const [device] = await hid.requestDevice({ filters: WHEEL_PRODUCTS.map((productId) => ({ vendorId: LOGITECH_VENDOR_ID, productId })) });
      if (!device) return false;
      return await this.openHid(device);
    } catch (e) {
      this.error = e instanceof Error ? e.message : String(e);
      return false;
    }
  }

  /** Use a gamepad-API wheel (input only) when nothing else is connected. */
  useGamepad(index: number): void {
    const pad = navigator.getGamepads?.()[index];
    if (pad) this.use(new GamepadSource(pad.index, pad.id));
  }

  async disconnect(): Promise<void> {
    const hid = this.hid;
    this.use(undefined);
    await hid?.close();
  }

  private async openHid(device: HIDDevice): Promise<boolean> {
    // Picked the wheel we already have open (e.g. Connect on a compatibility-mode wheel): keep it.
    if (this.hid && this.hid.device === device && this.hid.connected) return true;
    const wheel = new HidWheel(device);
    try {
      await wheel.open();
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e);
      // On Linux this is almost always /dev/hidraw* permissions (see docs/g27.md).
      const advice = /Windows/.test(navigator.userAgent)
        ? 'On Windows, make sure Logitech G HUB is installed and sees the wheel, close other games using it, then replug it.'
        : 'On Linux, install the udev rule from docs/g27.md and replug the wheel.';
      this.error = `Couldn't open the wheel (${msg}). ${advice}`;
      this.onChange?.();
      return false;
    }
    if (this.hid && this.hid !== wheel) await this.hid.close();
    this.error = undefined;
    this.use(wheel);
    return true;
  }

  private use(source: ControlSource | undefined): void {
    this.source = source;
    this.hid = source instanceof HidWheel ? source : undefined;
    this.input = new WheelInput(loadProfile(source?.key ?? 'none', defaultProfile(source instanceof HidWheel ? source.device.productId : undefined)));
    this.syncVelocityAxis();
    this.state = undefined;
    if (this.hid) {
      if (this.driving) this.hid.start(wheelParams.rotation.value);
      else {
        this.hid.idle();
        this.hid.setRange(wheelParams.rotation.value);
      }
    }
    this.ensureTimer();
    this.onChange?.();
  }

  saveProfile(): void {
    if (this.source) saveProfile(this.source.key, this.input.profile);
    this.syncVelocityAxis();
  }

  private syncVelocityAxis(): void {
    const axis = this.input.profile.steer.axis;
    if (this.source instanceof HidWheel || this.source instanceof GamepadSource) this.source.velocityAxis = axis;
  }

  /** Read the wheel (call once per frame). */
  poll(): WheelInputState | undefined {
    const s = this.source;
    if (!s?.connected || !this.active) {
      this.state = undefined;
      return undefined;
    }
    s.poll?.();
    this.state = this.input.read(s.controls);
    return this.state;
  }

  /** Game starts or stops driving: forces on with our range, or idle centring in menus. */
  setDriving(on: boolean): void {
    if (on === this.driving) return;
    this.driving = on;
    if (!on) this.ffb.reset();
    if (this.hid?.connected) {
      if (on) this.hid.start(wheelParams.rotation.value);
      else this.hid.idle();
    }
  }

  /** Apply a changed rotation setting right away. */
  applyRange(): void {
    this.hid?.setRange(wheelParams.rotation.value);
  }

  /** Per-frame force feedback update from the car (after the physics steps). */
  updateFfb(inputs: FfbInputs, dt: number): void {
    const out = this.ffb.update(inputs, dt, performance.now() / 1000);
    if (this.driving && this.hasFfb) {
      this.hid!.setDamper(out.damper);
      this.hid!.setFriction(out.friction);
    }
  }

  /** Rev LEDs from the shift-light level; flashes at the limiter. */
  updateLeds(level: number, flash: boolean): void {
    if (!this.hasFfb) return;
    if (!wheelParams.leds.value || !this.driving) {
      this.hid!.setLeds(0);
      return;
    }
    this.ledFlash = flash ? Math.floor(performance.now() / 80) % 2 === 0 : false;
    this.hid!.setLeds(ledMask(level, this.ledFlash, flash ? 0 : 2));
  }

  /** Setup screen: push the wheel with `level` (game convention, + right) for `ms`. */
  testForce(level: number, ms: number): void {
    if (!this.hasFfb) return;
    if (!this.driving) this.hid!.start(wheelParams.rotation.value);
    this.override = level;
    this.overrideUntil = performance.now() + ms;
    this.overrideActive = true;
  }

  /**
   * Find which way a positive force turns this wheel: push it briefly and watch
   * the steering axis. Returns false if it didn't move (hands on the wheel, or
   * no force feedback).
   */
  async calibrateDirection(): Promise<boolean> {
    const hid = this.hid;
    if (!hid || !this.hasFfb || this.calibrating) return false;
    this.calibrating = true;
    try {
      return await this.measureDirection(hid);
    } finally {
      this.calibrating = false;
    }
  }

  private async measureDirection(hid: HidWheel): Promise<boolean> {
    const p = this.input.profile;
    const axis = p.steer.axis;
    hid.start(wheelParams.rotation.value);
    await new Promise((r) => setTimeout(r, 150));
    const before = hid.controls.axes[axis] ?? 0.5;
    // Raw (uncorrected) force: pre-multiplied so the tick's forceSign cancels out.
    const raw = 0.35;
    this.override = raw * p.forceSign;
    this.overrideUntil = performance.now() + 1500;
    this.overrideActive = true;
    hid.setForce(raw);
    // Wait until the rim has clearly moved (or give up): don't rely on a fixed delay.
    const start = performance.now();
    let after = before;
    while (performance.now() - start < 1200) {
      await new Promise((r) => setTimeout(r, 20));
      after = hid.controls.axes[axis] ?? before;
      if (Math.abs(after - before) > 0.03) break;
    }
    this.overrideUntil = 0;
    this.overrideActive = false;
    hid.setForce(0);
    if (!this.driving) hid.idle();
    const moved = after - before;
    if (Math.abs(moved) < 0.005) return false;
    // Positive raw force moved the axis by `moved`; right is towards `full`.
    const rawTurnsRight = Math.sign(moved) === Math.sign(p.steer.full - p.steer.rest);
    p.forceSign = rawTurnsRight ? 1 : -1;
    p.forceSignChecked = true;
    this.saveProfile();
    return true;
  }

  private ensureTimer(): void {
    if (this.timer !== undefined || typeof setInterval === 'undefined') return;
    this.timer = setInterval(() => this.tick(), FFB_TICK_MS);
  }

  private tick(): void {
    const hid = this.hid;
    if (!hid?.connected || !hid.native) return;
    const now = performance.now();
    if (now < this.overrideUntil) {
      // Test forces: game convention unless calibrating (already raw).
      hid.setForce(this.override * this.input.profile.forceSign);
      return;
    }
    if (this.overrideActive) {
      // A test push just ended: outside driving, drop it and bring the centring spring back.
      this.overrideActive = false;
      if (!this.driving) {
        hid.setForce(0);
        hid.idle();
      }
    }
    if (!this.driving) return;
    hid.setForce(this.ffb.sample(now / 1000) * this.input.profile.forceSign);
  }
}

/**
 * A pretend wheel for development without the hardware (?fakewheel for a G27,
 * ?fakewheel=g29 for a G29): scripts set its controls, and output reports are
 * logged so the force feedback can be checked end to end. Installs a fake navigator.hid.
 */
export interface FakeWheel {
  state: { wheel: number; pedals: [number, number, number]; buttons: boolean[] };
  sent: Uint8Array[];
  /** The simulated motor turns the rim with the force feedback; off when a script holds the wheel. */
  motor: boolean;
  /** Send an input report now (scripts that set `state` and want it read this frame). */
  push(): void;
}

export function installFakeWheel(model: FakeWheelModel = 'g27'): FakeWheel {
  const spec = FAKE_WHEELS[model];
  const state = { wheel: 0.5, pedals: [1, 1, 1] as [number, number, number], buttons: new Array<boolean>(spec.buttons).fill(false) };
  const sent: Uint8Array[] = [];
  const fake: FakeWheel = { state, sent, motor: true, push: () => report() };
  const listeners = new Set<(e: HIDInputReportEvent) => void>();
  let opened = false;
  let force = 0;
  const device = {
    vendorId: LOGITECH_VENDOR_ID,
    productId: spec.productId,
    productName: spec.productName,
    collections: spec.collections,
    get opened() {
      return opened;
    },
    open: async () => {
      opened = true;
    },
    close: async () => {
      opened = false;
    },
    sendReport: async (_id: number, data: BufferSource) => {
      const bytes = new Uint8Array(data instanceof ArrayBuffer ? data : (data as ArrayBufferView).buffer.slice(0));
      sent.push(bytes);
      if (sent.length > 2000) sent.splice(0, 1000);
      if (bytes[0] === 0x11) force = ((bytes[2] ?? 0x80) - 0x80) / 127;
      else if (bytes[0] === 0x13 || bytes[0] === 0xf3) force = 0;
    },
    addEventListener: (_t: string, l: (e: HIDInputReportEvent) => void) => listeners.add(l),
    removeEventListener: (_t: string, l: (e: HIDInputReportEvent) => void) => listeners.delete(l),
    dispatchEvent: () => true,
  } as unknown as HIDDevice;
  const hid = {
    getDevices: async () => [device],
    requestDevice: async () => [device],
    addEventListener: () => undefined,
  } as unknown as HID;
  Object.defineProperty(navigator, 'hid', { value: hid, configurable: true });

  // Simulated wheel: the motor nudges the rim (forces are visible as the wheel moving on its own).
  function report(): void {
    if (!opened) return;
    const bytes = spec.encode({ hat: -1, buttons: state.buttons, wheel: state.wheel, pedals: state.pedals });
    const data = new DataView(bytes.buffer);
    for (const l of listeners) l({ reportId: 0, data, device, timeStamp: performance.now() } as unknown as HIDInputReportEvent);
  }
  setInterval(() => {
    // The simulated motor treats + raw force as turning left (matching the default forceSign −1).
    if (opened && fake.motor) state.wheel = Math.min(1, Math.max(0, state.wheel - force * 0.02));
    report();
  }, 4);
  return fake;
}
